import React, { useState } from "react";
import { Link } from "react-router-dom";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { domainExpertise } from "../data/siteContent";
import "../styles/domain-pages.css";

const domainSignals = [
  {
    title: "Business context before technology",
    copy: "We map the reporting cycle, source ownership, internal controls and decision points before designing the data platform.",
  },
  {
    title: "Governed data for audit pressure",
    copy: "Our domain platforms focus on lineage, multi-system reconciliation and explainable reporting loops rather than dashboards alone.",
  },
  {
    title: "Reusable engineering patterns",
    copy: "The same technical backbone supports financial services, sustainability metrics, industrial operations and energy reporting use cases.",
  },
];

export default function DomainExpertisePage() {
  const [activeTab, setActiveTab] = useState(domainExpertise[0].id);
  const activeDomain =
    domainExpertise.find((domain) => domain.id === activeTab) || domainExpertise[0];

  return (
    <div className="site-page">
      <Seo
        title="Domain Expertise | Data Apparatus"
        description="Enterprise domain expertise for financial services, lending platforms, sustainability reporting, industrial operations data and energy data systems in Australia."
      />

      <section className="page-hero domain-hero">
        <div className="page-hero-copy">
          <span className="eyebrow">Domain expertise</span>
          <h1>Data engineering shaped around the way your business reports and operates.</h1>
          <p>
            Data Apparatus combines Microsoft cloud data platforms with practical domain
            knowledge across regulated lending structures, sustainability systems,
            industrial operations and energy reporting.
          </p>
          <div className="hero-actions">
            <Link to="/contact" className="primary-btn">
              Book a domain data review
            </Link>
            <Link to="/services" className="secondary-btn">
              View technical services
            </Link>
          </div>
        </div>
      </section>

      <section className="domain-matrix-section">
        <div className="domain-matrix">
          <div className="section-heading left">
            <span className="eyebrow">Specialist domains</span>
            <h2>Where data capability meets operational context.</h2>
            <p>
              Select a domain to see the reporting risks, integration patterns and
              practical platform deliverables that sit behind the technical services.
            </p>
          </div>

          <div className="domain-tabs" role="tablist" aria-label="Domain expertise">
            {domainExpertise.map((domain) => (
              <button
                type="button"
                key={domain.id}
                className={`domain-tab${activeTab === domain.id ? " active" : ""}`}
                onClick={() => setActiveTab(domain.id)}
                role="tab"
                aria-selected={activeTab === domain.id}
              >
                {domain.tabLabel}
              </button>
            ))}
          </div>

          <div className="domain-panel">
            <div className="domain-panel-copy">
              <span className="eyebrow">{activeDomain.tabLabel}</span>
              <h3>{activeDomain.title}</h3>
              <p className="domain-subtitle">{activeDomain.subtitle}</p>
              <p>{activeDomain.summary}</p>

              <div className="domain-tech-stack">
                <span>Engineered platforms</span>
                <div>
                  {activeDomain.tech.map((tech) => (
                    <i key={tech}>{tech}</i>
                  ))}
                </div>
              </div>
            </div>

            <div className="domain-deliverables">
              <span className="eyebrow">Core framework capabilities</span>
              <ul>
                {activeDomain.points.map((point, index) => {
                  const [title, ...descriptionParts] = point.split(": ");
                  const description = descriptionParts.join(": ");

                  return (
                    <li key={point}>
                      <em>{String(index + 1).padStart(2, "0")}</em>
                      <div>
                        <strong>{title}</strong>
                        <span>{description}</span>
                      </div>
                    </li>
                  );
                })}
              </ul>
              <Link to={activeDomain.href} className="secondary-btn">
                View detailed page
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="page-section">
        <div className="section-heading">
          <span className="eyebrow">How this supports the site story</span>
          <h2>Services explain delivery. Integrations explain platforms. Domains explain value.</h2>
        </div>
        <div className="outcome-grid">
          {domainSignals.map((item) => (
            <article className="domain-signal-card" key={item.title}>
              <h3>{item.title}</h3>
              <p>{item.copy}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="cta-band">
        <h2>Need data engineering that understands the reporting context?</h2>
        <p>
          Start with a review of systems, source data, controls and reporting outcomes.
        </p>
        <Link to="/contact" className="primary-btn">
          Discuss your domain
        </Link>
      </section>

      <Footer />
    </div>
  );
}
